import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerModal } from '../../../components/common/DatePickerModal';
import api from '../../../services/api';
import styles from '../ReportMobile.module.css';

interface ProfitReportData {
  product_code: string;
  product_name: string;
  total_merchandise: number;
  total_discount: number;
  revenue: number;
  total_cost: number;
  gross_profit: number;
  quantity_sold: number;
}

interface ProfitSummary {
  total_transactions: number;
  total_merchandise: number;
  total_discount: number;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
}

const ProfitReportMobile: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  // Date picker modal states
  const [startDateModalOpen, setStartDateModalOpen] = useState(false);
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs().startOf('month'));
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(dayjs().endOf('month'));

  // Fetch profit report data
  const { data: reportData, error, refetch, isLoading } = useQuery(
    ['profit-report-mobile', dateRange],
    async () => {
      const response = await api.get('/api/reports/profit-detail', {
        params: {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD')
        }
      });
      return response.data;
    },
    {
      enabled: !!dateRange[0] && !!dateRange[1],
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Profit report API error:', error);
        alert('Có lỗi xảy ra khi tải dữ liệu báo cáo');
      }
    }
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  // Calculate summary from data
  const calculateSummary = (data: ProfitReportData[]): ProfitSummary => {
    return data.reduce((summary, item) => ({
      total_transactions: summary.total_transactions + 1,
      total_merchandise: summary.total_merchandise + item.total_merchandise,
      total_discount: summary.total_discount + item.total_discount,
      total_revenue: summary.total_revenue + item.revenue,
      total_cost: summary.total_cost + item.total_cost,
      total_profit: summary.total_profit + item.gross_profit
    }), {
      total_transactions: 0,
      total_merchandise: 0,
      total_discount: 0,
      total_revenue: 0,
      total_cost: 0,
      total_profit: 0
    });
  };

  const reportItems = reportData?.data || [];
  const summary = calculateSummary(reportItems);

  // Handle date selection
  const handleStartDateSelect = (date: Dayjs) => {
    setSelectedStartDate(date);
    setDateRange([date, dateRange[1]]);
  };

  const handleEndDateSelect = (date: Dayjs) => {
    setSelectedEndDate(date);
    setDateRange([dateRange[0], date]);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className={styles.bgGray}>
      <div className={styles.stickyHeader}>
        <div className={styles.title}>Báo cáo lợi nhuận</div>
        <div className={styles.flexRow}>
          <button
            onClick={() => setStartDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedStartDate.format('DD/MM/YYYY')}
          </button>
          <span className={styles.textGray} style={{ alignSelf: 'center' }}>-</span>
          <button
            onClick={() => setEndDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedEndDate.format('DD/MM/YYYY')}
          </button>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className={styles.button}
        >
          {isLoading ? 'Đang tải...' : 'Xem báo cáo'}
        </button>
      </div>
      <div style={{ padding: 8 }}>
        {/* Tổng quan */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Tổng quan</div>
          <div className={styles.sectionTotal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Số giao dịch:</span>
              <strong>{summary.total_transactions}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tổng doanh thu:</span>
              <strong style={{ color: '#52c41a' }}>{formatCurrency(summary.total_revenue)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tổng giá vốn:</span>
              <strong style={{ color: '#ff4d4f' }}>{formatCurrency(summary.total_cost)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Tổng lợi nhuận:</span>
              <strong style={{ color: summary.total_profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                {formatCurrency(summary.total_profit)}
              </strong>
            </div>
          </div>
        </div>

        {/* Chi tiết lợi nhuận theo sản phẩm */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Chi tiết lợi nhuận theo sản phẩm</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table} style={{ minWidth: 600 }}>
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Mã SP</th>
                  <th>Tên sản phẩm</th>
                  <th>Doanh thu</th>
                  <th>Giá vốn</th>
                  <th>Lợi nhuận</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                      Đang tải dữ liệu...
                    </td>
                  </tr>
                ) : reportItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                      Không có dữ liệu trong khoảng thời gian đã chọn
                    </td>
                  </tr>
                ) : (
                  reportItems.map((item: ProfitReportData, index: number) => (
                    <tr key={item.product_code} className={index % 2 === 0 ? '' : styles.tableRowAlt}>
                      <td>{index + 1}</td>
                      <td>{item.product_code}</td>
                      <td>{item.product_name}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(item.revenue)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(item.total_cost)}</td>
                      <td style={{ textAlign: 'right', color: item.gross_profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                        {formatCurrency(item.gross_profit)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        
      </div>

      {/* Date Picker Modals */}
      <DatePickerModal
        isOpen={startDateModalOpen}
        onClose={() => setStartDateModalOpen(false)}
        onSelect={handleStartDateSelect}
        selectedDate={selectedStartDate}
        title="Chọn ngày bắt đầu"
      />
      <DatePickerModal
        isOpen={endDateModalOpen}
        onClose={() => setEndDateModalOpen(false)}
        onSelect={handleEndDateSelect}
        selectedDate={selectedEndDate}
        title="Chọn ngày kết thúc"
      />
    </div>
  );
};

export default ProfitReportMobile;
